'use client';

import { ElementComponents } from '@/components/sections/builder/builder-element';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';

import { WrapperTheme } from '@/components/sections/builder/wrapper';
import { passwordCollection, retrieveCollectionDetail } from '@/lib/actions/collection/action';
import { CommonElementDataType, ElementType, ThemeType } from '@/stores/builder';
import { IBuilder } from '@/types/site';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from '@/components/ui/use-toast';
import NotFoundPage from '@/components/sections/builder/not-found';
import CardProductSkeleton from '@/components/sections/card-loading';
import dynamic from 'next/dynamic';
import { HttpTypes } from '@/types';
import Cookies from 'js-cookie';
import { useCookies } from '@/app/cookie-provider';
import { Loader2 } from 'lucide-react';

const BuilderPreviewPage = dynamic(
  () => import('@/components/sections/builder/preview-page').then((mod) => mod.default),
  { ssr: false }
);

type PropsType = {
  data: IBuilder;
  slug: string;
  host: string;
  isPreview?: boolean;
  themeId?: string;
};

type FormPassword = {
  password: string;
};

const PasswordProtectionScheme = Yup.object().shape({
  password: Yup.string().required('Password is a required'),
});

export default function CollectionPageLive({ data, slug, host, isPreview, themeId }: PropsType) {
  const builder = (data?.data || {}) as { elements: ElementType[]; theme: ThemeType };

  const [privateCollection, setPrivateCollection] = React.useState<boolean>(false);

  const elements = (builder.elements || [])
    .filter(
      (element) =>
        !(element.data as CommonElementDataType)?.hidden &&
        !!ElementComponents[element.type]?.Element
    )
    .map((element) => ({
      ...element,
      childs: element.childs?.filter((child) => !(child.data as CommonElementDataType)?.hidden),
    }));

  const headerElements = elements?.filter((element) => element.group === 'header');
  const bodyElements = elements?.filter((element) => element.group === 'body_collections');
  const footerElements = elements?.filter((element) => element.group === 'footer');

  const headerElement = headerElements?.find((e) => e.type === 'header');
  const headerData = headerElement?.data as HeaderElementDataType;

  const { data: collectionItem, isLoading } = useQuery({
    queryKey: ['collection-detail', slug],
    queryFn: async () => (await retrieveCollectionDetail(slug))?.data,
    select: (data) => data?.data,
  });

  React.useEffect(() => {
    if (collectionItem) {
      setPrivateCollection(collectionItem.visibility === 'private' ? true : false);
    }
  }, [collectionItem]);

  React.useEffect(() => {
    if (!isPreview) return;
    window.parent?.postMessage(
      {
        type: 'iframe_loaded',
        previewPath: 'collections',
        themeId,
        slug: `collections/${slug}`,
      },
      '*'
    );
  }, [isPreview, slug, themeId]);

  if (!data || !builder.elements) return null;

  if (isPreview) return <BuilderPreviewPage groupBody='body_collections' />;

  return (
    <WrapperTheme theme={builder.theme}>
      <link rel='icon' href={headerData?.faviconUrl} type='image/x-icon' />
      <div id='wrapper-header'>
        {headerElements.map((element) => {
          const Element = ElementComponents[element.type].Element;
          if (Element) return <Element element={element} key={element.id} theme={builder.theme} />;
        })}
      </div>
      <div className='theme_layout_space_section flex-grow'>
        {isLoading ? (
          <div className='grid grid-cols-2 gap-3 p-4 md:grid-cols-3 lg:grid-cols-5 lg:p-10'>
            {Array.from({ length: 5 }).map((_, index) => (
              <CardProductSkeleton key={index} />
            ))}
          </div>
        ) : (
          <CollectionContent
            slug={slug}
            privateCollection={privateCollection}
            data={collectionItem}
            host={host}
            elements={bodyElements}
            preview={isPreview}
            theme={builder.theme}
          />
        )}
      </div>
      {footerElements.map((element) => {
        const Element = ElementComponents[element.type].Element;
        if (Element) return <Element element={element} key={element.id} />;
      })}
    </WrapperTheme>
  );
}

function CollectionContent({
  slug,
  privateCollection,
  data,
  host,
  theme,
  elements,
}: {
  slug: string;
  privateCollection: boolean;
  data: HttpTypes.StoreCollection;
  host: string;
  elements: ElementType[];
  preview?: boolean;
  theme?: ThemeType;
}) {
  const { isActiveSiteProtection } = useCookies();
  const isValidAccess = Cookies.get('_is_valid_access') === 'true';
  const isSlugValid = Cookies.get(`is_valid_slug_${slug}`) === 'true';

  if (slug !== 'all' && (!data || data.status !== 'Active')) return <NotFoundPage />;

  if (isActiveSiteProtection) {
    if (isValidAccess) {
      return elements.map((element) => {
        const Element = ElementComponents[element.type]?.Element;
        return Element ? (
          <Element key={element.id} element={element} data={data} host={host} theme={theme} />
        ) : null;
      });
    }
  } else {
    if (privateCollection && !isSlugValid) {
      return <PasswordForm slug={slug} />;
    }
  }

  return elements.map((element) => {
    const Element = ElementComponents[element.type]?.Element;
    return Element ? (
      <Element key={element.id} element={element} data={data} host={host} theme={theme} />
    ) : null;
  });
}

function PasswordForm({ slug }: { slug: string }) {
  const handleOnSubmit = async (
    values: FormPassword,
    { setErrors }: FormikHelpers<FormPassword>
  ) => {
    try {
      const host = window.location.host;

      const res = await passwordCollection(values.password, host);
      if (res?.success) {
        Cookies.set(`is_valid_slug_${slug}`, 'true', { expires: 7 });
        toast({ title: res.data?.message, variant: 'success', duration: 1500 });
        setTimeout(() => {
          window.location.reload();
        }, 1600);
      } else {
        setErrors({ password: res?.data?.message });
      }
    } catch {
      toast({ title: 'Error', description: 'Something went wrong.', variant: 'destructive' });
    }
  };
  return (
    <div className='grid place-items-center py-4'>
      <div>
        <h1 className='theme_font_heading mb-4 text-3xl'>Password Protection</h1>
        <Formik
          key='passwordProtectCollection'
          initialValues={{ password: '' }}
          validationSchema={PasswordProtectionScheme}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, handleSubmit, handleChange, handleBlur, touched, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <div className='flex w-full flex-col'>
                <Input
                  name='password'
                  type='password'
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder='Password'
                  classNameWrapper='w-full mt-4'
                  required
                />
                {errors.password && touched.password && (
                  <span className='mt-1 text-red-500'>{errors.password}</span>
                )}
              </div>
              <Button className='theme_button mt-4 w-full text-sm' type='submit'>
                {isSubmitting ? <Loader2 className='h-4 w-4' /> : 'Submit'}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
